import { type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TrendingDown, TrendingUp } from "lucide-react";
import "./DashboardKpiCard.scss";
import DefinitionTooltip, { type DefinitionTooltipInfo } from "../DefinitionTooltip";
import {
  formatChangePercentDisplay,
  formatDashboardValueNode,
} from "src/utils/formatDashboardValue";
import { resolveDashboardChangePercent } from "src/utils/dashboardChangeFallback";
import { isolateNumericRuns } from "src/utils/numberFormat";
import { useLanguage } from "src/hooks/UseLanguage";

type DashboardKpiCardTone = "positive" | "negative" | "neutral";
type DashboardKpiCardTrendPolarity = "higher-is-better" | "lower-is-better";

function metricLabelForAria(title: ReactNode, explicit?: string): string {
  if (explicit) {
    return explicit;
  }
  if (typeof title === "string" || typeof title === "number") {
    return String(title);
  }
  return "Metric";
}

interface DashboardKpiCardProps {
  title: ReactNode;
  value: ReactNode;
  /** Muted line under the value */
  sublabel?: ReactNode;
  /** Shown when there is no numeric trend and no `footer` */
  description?: ReactNode;
  tone?: DashboardKpiCardTone;
  icon?: ReactNode;
  headerAccessory?: ReactNode;
  footer?: ReactNode;
  tooltip?: DefinitionTooltipInfo;
  tooltipAlign?: "start" | "end";
  metricLabelForTooltip?: string;
  formulaTitle?: string;
  changePercent?: number | null;
  changePercentFormattedValue?: string;
  trendPolarity?: DashboardKpiCardTrendPolarity;
  disableChangeFallback?: boolean;
  /** When true with fallback enabled, API `0` uses the placeholder band (see `resolveDashboardChangePercent`). */
  changePercentTreatZeroAsMissing?: boolean;
  /** When set (e.g. `1`), resolved trend % is clamped to this absolute max (sign preserved). */
  changePercentClampAbsMax?: number;
  /** Optional seed for placeholder % when title is not a plain string. */
  changePercentFallbackSeed?: string | number;
  /** Defaults to "vs last period" */
  comparisonLabel?: string;
  trendOutcomeLabel?: string;
  noTrendLabel?: string;
  noTrendTooltip?: string;
  /** When true, omit the "Trend unavailable" fallback (e.g. snapshot KPIs with no period comparison). */
  hideNoTrendFallback?: boolean;
  uppercaseTitle?: boolean;
  className?: string;
}

const DashboardKpiCard: FC<DashboardKpiCardProps> = ({
  title,
  value,
  sublabel,
  description,
  tone = "neutral",
  icon,
  headerAccessory,
  footer,
  tooltip,
  tooltipAlign = "start",
  metricLabelForTooltip,
  formulaTitle = "Formula",
  changePercent,
  changePercentFormattedValue,
  trendPolarity = "higher-is-better",
  disableChangeFallback = true,
  changePercentTreatZeroAsMissing = true,
  changePercentClampAbsMax,
  changePercentFallbackSeed,
  comparisonLabel = "vs last period",
  trendOutcomeLabel,
  noTrendLabel = "Trend unavailable",
  noTrendTooltip = "No earlier data available to compare this range.",
  hideNoTrendFallback = false,
  uppercaseTitle = true,
  className = "",
}) => {
  const { i18n } = useTranslation();
  const { language } = useLanguage();
  const displayComparisonLabel = isolateNumericRuns(comparisonLabel, language);

  const trendSeed =
    changePercentFallbackSeed ??
    metricLabelForTooltip ??
    (typeof title === "string" || typeof title === "number" ? String(title) : "dashboard-kpi");

  const effectiveChangePercent = disableChangeFallback
    ? typeof changePercent === "number" &&
      Number.isFinite(changePercent) &&
      !(changePercentTreatZeroAsMissing && changePercent === 0)
      ? changePercent
      : undefined
    : resolveDashboardChangePercent(changePercent, trendSeed, {
        treatZeroAsMissing: changePercentTreatZeroAsMissing,
        clampAbsMax: changePercentClampAbsMax,
      });

  const hasTrend =
    typeof effectiveChangePercent === "number" && Number.isFinite(effectiveChangePercent);
  const isFlat = hasTrend && effectiveChangePercent === 0;
  const isIncrease = hasTrend ? effectiveChangePercent >= 0 : true;
  const isPositiveOutcome =
    hasTrend && trendPolarity === "lower-is-better" ? effectiveChangePercent <= 0 : isIncrease;
  const tooltipLabel = metricLabelForAria(title, metricLabelForTooltip);
  const changeDisplay = hasTrend
    ? changePercentFormattedValue ??
      formatChangePercentDisplay(effectiveChangePercent, i18n.language)
    : undefined;
  const changeDirectionLabel = hasTrend
    ? `${changeDisplay} ${
        isFlat ? "no change" : isIncrease ? "increase" : "decrease"
      } ${comparisonLabel}${trendOutcomeLabel ? `. ${trendOutcomeLabel}` : ""}`
    : undefined;
  const noTrendContent =
    !hideNoTrendFallback && !hasTrend && !footer ? (
      <span className="dashboard-kpi-card__trend-unavailable">
        <span className="dashboard-kpi-card__trend-unavailable-label">{noTrendLabel}</span>
        <DefinitionTooltip
          label={noTrendLabel}
          info={{ description: noTrendTooltip }}
          align={tooltipAlign}
        />
      </span>
    ) : null;

  return (
    <article className={`dashboard-kpi-card ${className}`.trim()}>
      <div className="dashboard-kpi-card__header">
        <div className="dashboard-kpi-card__header-main">
          <div className="dashboard-kpi-card__title-row">
            <p
              className={`dashboard-kpi-card__title ${
                uppercaseTitle ? "dashboard-kpi-card__title--uppercase" : ""
              }`.trim()}
            >
              {title}
            </p>
            {tooltip ? (
              <DefinitionTooltip
                label={tooltipLabel}
                info={tooltip}
                align={tooltipAlign}
                formulaTitle={formulaTitle}
              />
            ) : null}
          </div>
          <div className="dashboard-kpi-card__value">{formatDashboardValueNode(value)}</div>
          {sublabel ? <div className="dashboard-kpi-card__sublabel">{sublabel}</div> : null}
        </div>

        {headerAccessory ? (
          <div className="dashboard-kpi-card__accessory">{headerAccessory}</div>
        ) : icon ? (
          <div className="dashboard-kpi-card__icon" aria-hidden>
            {icon}
          </div>
        ) : null}
      </div>

      {hasTrend ? (
        <div className="dashboard-kpi-card__period">
          <span
            className={`dashboard-kpi-card__change dashboard-kpi-card__change--${
              isFlat ? "neutral" : isPositiveOutcome ? "positive" : "negative"
            }`}
            aria-label={changeDirectionLabel}
            title={changeDirectionLabel}
          >
            {isFlat ? null : isIncrease ? (
              <TrendingUp size={14} aria-hidden />
            ) : (
              <TrendingDown size={14} aria-hidden />
            )}
            {changeDisplay}
          </span>
          <span className="dashboard-kpi-card__comparison">{displayComparisonLabel}</span>
        </div>
      ) : footer ? (
        <div className="dashboard-kpi-card__footer">{footer}</div>
      ) : noTrendContent ? (
        <div className="dashboard-kpi-card__footer">{noTrendContent}</div>
      ) : description ? (
        <div
          className={`dashboard-kpi-card__description dashboard-kpi-card__description--${tone}`}
        >
          {description}
        </div>
      ) : null}
    </article>
  );
};

export type { DashboardKpiCardProps };
export default DashboardKpiCard;
